/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useMemo, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Package, 
  Building2, 
  Flame, 
  FlaskConical,
  Eye, 
  LogOut, 
  MapPin, 
  Search, 
  Printer, 
  CheckCircle2, 
  RefreshCw, 
  Camera, 
  ChevronRight, 
  X, 
  ArrowRight,
  QrCode,
  ShieldCheck,
  Building,
  User,
  Truck,
  FileText,
  Clock,
  Sparkles,
  Check,
  Upload,
  Trash2,
  Barcode,
  Activity,
  Compass,
  ArrowLeft
} from 'lucide-react';
import { Folio, ProcessStage, Client, UserProfile, ProcessType, CostCenter } from '../types.ts';
import { downloadFolioPDF } from '../utils/pdfGenerator.ts';
import { dataService } from '../lib/dataService.ts';

interface TraceabilityStationManagerProps {
  folios: Folio[];
  clients?: Client[];
  currentUser?: UserProfile | null;
  onUpdateFolio?: (updated: Folio) => void;
  isOnline?: boolean;
  selectedClientId?: string;
  initialFaseNumber?: number;
  initialFolioId?: string | null;
  isPublicView?: boolean;
  onBackToApp?: () => void;
}

interface StageDef {
  faseNumber: number;
  stageKey: ProcessStage;
  label: string;
  subtitle: string;
  icon: React.ElementType;
}

const STAGES: StageDef[] = [
  {
    faseNumber: 1,
    stageKey: 'RECOLECCION',
    label: 'Recolección',
    subtitle: 'Preparación y recolección técnica en origen',
    icon: Package
  },
  {
    faseNumber: 2,
    stageKey: 'ENTRADA_CEDIS',
    label: 'Entrada CEDIS',
    subtitle: 'Recepción, descarga y pesaje en patio CEDIS',
    icon: Building2
  },
  {
    faseNumber: 3,
    stageKey: 'DECAPADO_TERMICO_QUIMICO',
    label: 'Decapado',
    subtitle: 'Tratamiento químico de remoción de óxidos',
    icon: Flame
  },
  {
    faseNumber: 4,
    stageKey: 'INSPECCION',
    label: 'Inspección',
    subtitle: 'Control de calidad final y liberación técnica',
    icon: Eye
  },
  {
    faseNumber: 5,
    stageKey: 'SALIDA_CEDIS',
    label: 'Salida CEDIS',
    subtitle: 'Preparación de embarque y despacho desde CEDIS',
    icon: LogOut
  },
  {
    faseNumber: 6,
    stageKey: 'ENTREGA_PLANTA',
    label: 'Entrega Planta',
    subtitle: 'Arribo y entrega física certificada en destino',
    icon: MapPin
  }
];

export default function TraceabilityStationManager({
  folios,
  clients = [],
  currentUser,
  onUpdateFolio,
  isOnline = true,
  selectedClientId: initialClientIdProp,
  initialFaseNumber,
  initialFolioId,
  isPublicView,
  onBackToApp
}: TraceabilityStationManagerProps) {

  // Selected Client State
  const [selectedClientId, setSelectedClientId] = useState<string>(() => {
    if (initialClientIdProp) return initialClientIdProp;
    if (clients && clients.length > 0) {
      return clients[0].id;
    }
    return 'ALL';
  });

  // Sync selectedClientId state when prop updates
  useEffect(() => {
    if (initialClientIdProp) {
      setSelectedClientId(initialClientIdProp);
    }
  }, [initialClientIdProp]);

  // Selected Stage (Fase 1..6 or 0 for ALL)
  const [selectedFaseNumber, setSelectedFaseNumber] = useState<number>(() => {
    if (initialFaseNumber !== undefined && initialFaseNumber >= 0 && initialFaseNumber <= 6) return initialFaseNumber;
    return 1;
  });
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isSyncing, setIsSyncing] = useState<boolean>(false);
  const [lastSyncTime, setLastSyncTime] = useState<string>(new Date().toLocaleTimeString());

  // Active Folio selected in floor control screen
  const [selectedFolioId, setSelectedFolioId] = useState<string | null>(initialFolioId || null);

  useEffect(() => {
    if (initialFaseNumber !== undefined && initialFaseNumber >= 0 && initialFaseNumber <= 6) {
      setSelectedFaseNumber(initialFaseNumber);
    }
  }, [initialFaseNumber]);

  useEffect(() => {
    if (initialFolioId) {
      setSelectedFolioId(initialFolioId);
      const matched = folios.find(f => f.id.trim().toUpperCase() === initialFolioId.trim().toUpperCase());
      if (matched) {
        let stageNum = 1;
        if (matched.status === 'RECOLECCION') stageNum = 1;
        else if (matched.status === 'ENTRADA_CEDIS') stageNum = 2;
        else if (matched.status === 'DECAPADO_TERMICO_QUIMICO' || matched.status === 'DECAPADO_LAVADO') stageNum = 3;
        else if (matched.status === 'INSPECCION' || matched.status === 'RETRABAJO') stageNum = 4;
        else if (matched.status === 'SALIDA_CEDIS') stageNum = 5;
        else if (matched.status === 'ENTREGA_PLANTA') stageNum = 6;
        setSelectedFaseNumber(stageNum);
      }
    }
  }, [initialFolioId, folios]);

  // Form Fields State for Floor Screen
  const [operatorName, setOperatorName] = useState<string>(currentUser?.name || 'PEDRO BORJA');
  const [transitionNotes, setTransitionNotes] = useState<string>('');
  const [evidencePhotos, setEvidencePhotos] = useState<string[]>([]);
  const [isCameraActive, setIsCameraActive] = useState<boolean>(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  // Live Server Time string
  const [liveTimeString, setLiveTimeString] = useState<string>('');

  // Folio Details Modal State
  const [showFolioDetailModal, setShowFolioDetailModal] = useState<boolean>(false);

  // Phase change animation modal state
  const [animationModalData, setAnimationModalData] = useState<{
    folio: Folio;
    oldStageLabel: string;
    nextStageLabel: string;
    operator: string;
  } | null>(null);

  // Check URL params on mount (e.g., when scanned via QR code ?terminal=Fase3)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const terminalParam = params.get('terminal') || params.get('fase') || params.get('stage');
    const clientParam = params.get('client');

    if (terminalParam) {
      const num = parseInt(terminalParam.replace(/\D/g, ''), 10);
      if (!isNaN(num) && num >= 1 && num <= 6) {
        setSelectedFaseNumber(num);
      }
    }

    if (clientParam && clientParam !== 'ALL') {
      setSelectedClientId(clientParam);
    }
  }, []);

  // Update live clock every second
  useEffect(() => {
    const updateClock = () => {
      const now = new Date();
      setLiveTimeString(now.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true }));
    };
    updateClock();
    const timer = setInterval(updateClock, 1000);
    return () => clearInterval(timer);
  }, []);

  // Load cost centers for address resolution
  const [costCentersList, setCostCentersList] = useState<CostCenter[]>([]);

  useEffect(() => {
    dataService.getCostCenters().then(ccs => {
      if (Array.isArray(ccs)) {
        setCostCentersList(ccs);
      }
    }).catch(() => {});
  }, []);

  // Sync operator name when currentUser updates
  useEffect(() => {
    if (currentUser?.name && operatorName === 'PEDRO BORJA') {
      setOperatorName(currentUser.name);
    }
  }, [currentUser]);

  // Active Client Object
  const activeClient = useMemo(() => {
    if (selectedClientId === 'ALL') return null;
    return clients.find(c => 
      c.id === selectedClientId || 
      c.code === selectedClientId || 
      c.name === selectedClientId
    ) || clients[0] || null;
  }, [clients, selectedClientId]);

  // Filtered Folios by selected client
  const clientFolios = useMemo(() => {
    if (selectedClientId === 'ALL' || !activeClient) return folios;
    return folios.filter(f => 
      f.clientId === activeClient.id || 
      f.clientName === activeClient.name ||
      (activeClient.code && (f.clientId === activeClient.code || f.clientName === activeClient.code)) ||
      (f.clientId && activeClient.id && f.clientId.toLowerCase() === activeClient.id.toLowerCase()) ||
      (f.clientName && activeClient.name && f.clientName.toLowerCase() === activeClient.name.toLowerCase())
    );
  }, [folios, activeClient, selectedClientId]);

  // Count of folios in each stage for badge counts
  const stageCounts = useMemo(() => {
    const counts: Record<number, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0 };

    clientFolios.forEach(f => {
      if (f.status === 'RECOLECCION') counts[1]++;
      else if (f.status === 'ENTRADA_CEDIS') counts[2]++;
      else if (f.status === 'DECAPADO_TERMICO_QUIMICO' || f.status === 'DECAPADO_LAVADO') counts[3]++;
      else if (f.status === 'INSPECCION' || f.status === 'RETRABAJO') counts[4]++;
      else if (f.status === 'SALIDA_CEDIS') counts[5]++;
      else if (f.status === 'ENTREGA_PLANTA' || f.status === 'DEVOLUCION') counts[6]++;
    });

    return counts;
  }, [clientFolios]);

  // Current selected stage definition
  const currentStageDef = useMemo(() => {
    if (selectedFaseNumber === 0) {
      return {
        faseNumber: 0,
        stageKey: 'RECOLECCION' as ProcessStage,
        label: 'Todas las Fases (Vista General)',
        subtitle: 'Visión consolidada de todos los folios y su estado actual de trazabilidad',
        icon: Compass
      };
    }
    return STAGES.find(s => s.faseNumber === selectedFaseNumber) || STAGES[0];
  }, [selectedFaseNumber]);

  // Filtered Folios for the active Stage & Search Query
  const stageFolios = useMemo(() => {
    return clientFolios.filter(f => {
      // 1. Global Search across ALL phases when search query is entered
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        const rawNumber = q.replace(/^#/, '').replace(/^f-?/, '').trim();
        const fid = (f.id || '').toLowerCase();
        const fidClean = fid.replace(/^#/, '').replace(/^f-?/, '').trim();

        const idMatch = fid.includes(q) || (rawNumber.length > 0 && fidClean.includes(rawNumber));
        const matMatch = f.items && f.items.some(it => (it.description || '').toLowerCase().includes(q));
        const clientMatch = (f.clientName || '').toLowerCase().includes(q);
        const driverMatch = (f.driverName || '').toLowerCase().includes(q);
        const destMatch = (f.destination || '').toLowerCase().includes(q);

        return idMatch || matMatch || clientMatch || driverMatch || destMatch;
      }

      // 2. All phases mode
      if (selectedFaseNumber === 0) {
        return true;
      }

      // 3. Stage match per selected stage
      if (selectedFaseNumber === 1) return f.status === 'RECOLECCION';
      if (selectedFaseNumber === 2) return f.status === 'ENTRADA_CEDIS';
      if (selectedFaseNumber === 3) return f.status === 'DECAPADO_TERMICO_QUIMICO' || f.status === 'DECAPADO_LAVADO';
      if (selectedFaseNumber === 4) return f.status === 'INSPECCION' || f.status === 'RETRABAJO';
      if (selectedFaseNumber === 5) return f.status === 'SALIDA_CEDIS';
      if (selectedFaseNumber === 6) return f.status === 'ENTREGA_PLANTA' || f.status === 'DEVOLUCION';

      return true;
    });
  }, [clientFolios, selectedFaseNumber, searchQuery]);

  // Set default active folio when stage or stageFolios changes
  useEffect(() => {
    if (stageFolios.length > 0) {
      if (!selectedFolioId || !stageFolios.some(f => f.id === selectedFolioId)) {
        setSelectedFolioId(stageFolios[0].id);
      }
    } else {
      setSelectedFolioId(null);
    }
  }, [stageFolios, selectedFaseNumber]);

  // Currently active selected Folio object
  const activeFolio = useMemo(() => {
    return stageFolios.find(f => f.id === selectedFolioId) || stageFolios[0] || null;
  }, [stageFolios, selectedFolioId]);

  // Resolve patio / cost center address dynamically or default to San Luis Potosí cost center
  const resolvePatioAddress = (folio?: Folio | null): string => {
    if (!folio) return "Bruselas 78, Parque Industrial, Logistik II, 79526 Valle de San Francisco, S.L.P.";
    if ((folio as any).costCenterAddress) return (folio as any).costCenterAddress;
    if (folio.costCenterId) {
      const found = costCentersList.find(c => c.id === folio.costCenterId || c.code === folio.costCenterId);
      if (found?.address) return found.address;
    }
    if (folio.costCenterName) {
      const found = costCentersList.find(c => c.name.toLowerCase() === folio.costCenterName?.toLowerCase());
      if (found?.address) return found.address;
    }
    return "Bruselas 78, Parque Industrial, Logistik II, 79526 Valle de San Francisco, S.L.P.";
  };

  // Helper to compute next process stage
  const getNextStage = (current: ProcessStage, hasGranallado?: boolean, processType?: ProcessType): ProcessStage => {
    const isGranallado = processType === 'DECAPADO_GRANALLADORA' || !!hasGranallado;
    switch (current) {
      case 'RECOLECCION': return 'ENTRADA_CEDIS';
      case 'ENTRADA_CEDIS': return 'DECAPADO_TERMICO_QUIMICO';
      case 'DECAPADO_TERMICO_QUIMICO': return isGranallado ? 'INSPECCION' : 'DECAPADO_LAVADO';
      case 'DECAPADO_LAVADO': return 'INSPECCION';
      case 'INSPECCION': return 'SALIDA_CEDIS';
      case 'RETRABAJO': return 'INSPECCION';
      case 'SALIDA_CEDIS': return 'ENTREGA_PLANTA';
      case 'ENTREGA_PLANTA': return 'ENTREGA_PLANTA';
      default: return 'ENTRADA_CEDIS';
    }
  };

  const getStageLabel = (stageKey: ProcessStage) => {
    switch (stageKey) {
      case 'RECOLECCION': return 'RECOLECCIÓN';
      case 'ENTRADA_CEDIS': return 'ENTRADA CEDIS';
      case 'DECAPADO_TERMICO_QUIMICO': return 'DECAPADO';
      case 'DECAPADO_LAVADO': return 'DECAPADO / LAVADO';
      case 'INSPECCION': return 'INSPECCIÓN';
      case 'RETRABAJO': return 'RETRABAJO';
      case 'SALIDA_CEDIS': return 'SALIDA CEDIS';
      case 'ENTREGA_PLANTA': return 'ENTREGA PLANTA';
      default: return stageKey.replace(/_/g, ' ');
    }
  };

  // Sync action
  const handleSync = () => {
    setIsSyncing(true);
    setTimeout(() => {
      setIsSyncing(false);
      setLastSyncTime(new Date().toLocaleTimeString());
    }, 700);
  };

  // Printable station card generator
  const handlePrintStationCard = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(
      `${window.location.origin}${window.location.pathname}?terminal=Fase${selectedFaseNumber}&client=${activeClient?.id || 'ALL'}`
    )}`;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Tarjeta de Control - Estación ${currentStageDef.label}</title>
          <style>
            @page { size: letter portrait; margin: 0.5in; }
            body { font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; text-align: center; color: #1e293b; padding: 20px; }
            .card { border: 3px solid #0f172a; border-radius: 20px; padding: 40px; max-w: 600px; margin: 0 auto; box-shadow: 0 10px 25px rgba(0,0,0,0.1); }
            .header-red { color: #dc2626; font-size: 20px; font-weight: 900; letter-spacing: 1px; margin-bottom: 5px; text-transform: uppercase; }
            .station-title { font-size: 28px; font-weight: 900; color: #0f172a; margin: 10px 0; border-bottom: 2px solid #e2e8f0; padding-bottom: 15px; }
            .client-text { font-size: 14px; color: #64748b; font-weight: 700; margin-bottom: 25px; }
            .qr-box { margin: 20px auto; width: 280px; height: 280px; padding: 15px; border: 2px solid #e2e8f0; border-radius: 16px; background: #fff; }
            .qr-box img { width: 100%; height: 100%; }
            .footer-note { font-size: 11px; color: #94a3b8; line-height: 1.5; margin-top: 30px; border-top: 1px dashed #cbd5e1; padding-top: 15px; }
          </style>
        </head>
        <body>
          <div class="card">
            <div class="header-red">SIC PRO SNF INDUSTRIAL SERVICES</div>
            <div class="station-title">ESTACIÓN: ${currentStageDef.label.toUpperCase()}</div>
            <div class="client-text">Escanear para cargar folios de: <strong>${activeClient ? activeClient.name : 'TODOS LOS CLIENTES'}</strong></div>
            <div class="qr-box">
              <img src="${qrUrl}" alt="QR Estación" />
            </div>
            <div class="footer-note">
              Validez operacional garantizada. Al escanear con su tableta o celular institucional de piso, se desplegarán todos los folios en cuarentena para su verificación y registro fotográfico.
            </div>
          </div>
          <script>
            window.onload = function() { window.print(); };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  // Camera stream controls
  const startCamera = async () => {
    setIsCameraActive(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.warn('Camera permission blocked or unavailable:', err);
      // Fallback base64 photo simulation if camera not granted in iframe
      const samplePhoto = 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=600&auto=format&fit=crop&q=60';
      setEvidencePhotos(prev => [...prev.slice(0, 6), samplePhoto]);
      setIsCameraActive(false);
    }
  };

  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream;
      stream.getTracks().forEach(track => track.stop());
    }
    setIsCameraActive(false);
  };

  const takePhoto = () => {
    if (videoRef.current) {
      const canvas = document.createElement('canvas');
      canvas.width = videoRef.current.videoWidth || 640;
      canvas.height = videoRef.current.videoHeight || 480;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL('image/jpeg');
        setEvidencePhotos(prev => [...prev.slice(0, 6), dataUrl]);
      }
    }
    stopCamera();
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    Array.from(files).forEach((file: File) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (typeof reader.result === 'string') {
          setEvidencePhotos(prev => {
            if (prev.length >= 7) return prev;
            return [...prev, reader.result as string];
          });
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const removePhoto = (index: number) => {
    setEvidencePhotos(prev => prev.filter((_, i) => i !== index));
  };

  // EXECUTE PHASE TRANSITION WITH ANIMATION
  const handleConfirmTransition = () => {
    if (!activeFolio || !onUpdateFolio) return;

    const nextStage = getNextStage(activeFolio.status, activeFolio.hasGranallado, activeFolio.processType);
    const oldStageLabel = getStageLabel(activeFolio.status);
    const nextStageLabel = getStageLabel(nextStage);

    const newHistoryEntry = {
      stage: nextStage,
      timestamp: new Date().toISOString(),
      operatorName: operatorName.trim() || 'PEDRO BORJA',
      notes: transitionNotes.trim() || `Transición de estado autorizada y firmada en piso en estación ${currentStageDef.label}.`
    };

    const updatedFolio: Folio = {
      ...activeFolio,
      status: nextStage,
      history: [...activeFolio.history, newHistoryEntry],
      photoUrl: evidencePhotos[0] || activeFolio.photoUrl
    };

    // Trigger Phase Animation Modal
    setAnimationModalData({
      folio: updatedFolio,
      oldStageLabel,
      nextStageLabel,
      operator: operatorName.trim() || 'PEDRO BORJA'
    });

    // Save update to state / database
    onUpdateFolio(updatedFolio);

    // Reset local form inputs
    setTransitionNotes('');
    setEvidencePhotos([]);
  };

  return (
    <div className="space-y-5 font-sans selection:bg-red-500/20 selection:text-red-900 pb-32 sm:pb-16 touch-pan-y">
      
      {/* 1. TOP HEADER BANNER */}
      <div className="bg-[#1a1b1f] text-white rounded-3xl p-5 md:p-6 shadow-2xl relative overflow-hidden border border-slate-800">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 relative z-10">
          
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              {onBackToApp && (
                <button
                  type="button"
                  onClick={onBackToApp}
                  className="p-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white rounded-xl transition-all border border-slate-700 cursor-pointer flex items-center gap-1.5 text-xs font-bold"
                  title="Volver al Portal Administrativo"
                >
                  <ArrowLeft className="h-4 w-4" />
                  <span className="hidden sm:inline">Volver</span>
                </button>
              )}
              <span className="px-3 py-0.5 bg-emerald-950/80 border border-emerald-600/80 text-emerald-400 text-[10px] font-black uppercase rounded-full tracking-wider">
                TERMINAL OPERATIVA EN PISO
              </span>
            </div>

            <h1 className="text-2xl md:text-3xl font-black text-white tracking-tight font-display flex items-center gap-3">
              Trazabilidad Dinámica y Despacho QR
            </h1>

            <p className="text-xs text-slate-300 max-w-4xl leading-relaxed">
              Gestión en tiempo real de etapas de decapado. Genere credenciales QR por proceso para que el personal obrero escanee, liste folios activos, tome evidencia fotográfica en patio y autorice transiciones ágiles en seco.
            </p>
          </div>

          {/* Right Status & Sync */}
          <div className="flex flex-wrap items-center gap-3 shrink-0">
            <div className="bg-[#121316] border border-slate-800 px-3.5 py-2 rounded-2xl flex items-center gap-2.5">
              <div className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse"></div>
              <div className="text-[10px] font-mono">
                <span className="text-slate-400 block font-semibold leading-none uppercase">PERSISTENCIA ACTIVA</span>
                <span className="text-slate-200 font-extrabold leading-tight">Firebase Firestore Cloud</span>
              </div>
            </div>

            <button
              onClick={handleSync}
              disabled={isSyncing}
              className="bg-[#2d2e33] hover:bg-slate-700 active:scale-95 text-white font-extrabold text-xs px-4 py-2.5 rounded-xl transition-all flex items-center gap-2 cursor-pointer border border-slate-600/60 shadow-md"
            >
              <RefreshCw className={`h-4 w-4 text-slate-300 ${isSyncing ? 'animate-spin' : ''}`} />
              SINCRONIZAR
            </button>
          </div>

        </div>
      </div>

      {/* 2. CLIENT SELECTOR BAR */}
      <div className="bg-white/95 backdrop-blur-md rounded-2xl p-4 sm:p-5 border border-slate-200/90 shadow-md flex flex-col sm:flex-row sm:items-center justify-between gap-4 md:sticky md:top-2 z-20 relative">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-slate-100 text-slate-700 flex items-center justify-center shrink-0 border border-slate-200">
            <Building2 className="h-5 w-5" />
          </div>

          <div>
            <span className="text-[10px] font-bold text-slate-400 tracking-wider uppercase block">CLIENTE SELECCIONADO</span>
            <select
              value={selectedClientId}
              onChange={(e) => setSelectedClientId(e.target.value)}
              className="bg-slate-50 border border-slate-200 text-slate-900 font-extrabold text-sm rounded-lg px-2.5 py-1 focus:outline-hidden focus:ring-2 focus:ring-red-500 cursor-pointer"
            >
              {clients.map(c => (
                <option key={c.id} value={c.id}>
                  {c.name} ({c.code || '01'})
                </option>
              ))}
              <option value="ALL">TODOS LOS CLIENTES</option>
            </select>
          </div>
        </div>

        {/* Counter Badge */}
        <div className="bg-slate-50 border border-slate-200/80 px-4 py-2 rounded-2xl font-bold text-slate-800 self-start sm:self-auto">
          <span className="text-red-700 font-black text-sm mr-1.5">{clientFolios.length}</span>
          <span className="text-slate-600 text-xs">Folios totales de carga asignados</span>
        </div>
      </div>

      {/* 3. MAIN GRID 2 COLUMNS (Left Stage QR Cards / Right Floor Control Screen) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-start">
        
        {/* LEFT COLUMN: QR CERTIFICADOS POR ETAPA (Col 4) */}
        <div className="lg:col-span-4 space-y-5 lg:sticky lg:top-[75px] self-start z-20">
          
          <div className="bg-[#1e1e24] text-white rounded-3xl p-5 border border-slate-800 shadow-xl space-y-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <QrCode className="h-4 w-4 text-cyan-400" />
                <h3 className="text-xs font-black uppercase tracking-wider text-slate-200">
                  CÓDIGOS QR CERTIFICADOS POR ETAPA
                </h3>
              </div>
              <p className="text-[11px] text-slate-400 leading-relaxed">
                Cada proceso en piso cuenta con una clave QR satelital. Escanee el código para ingresar directo a la terminal operativa correspondiente, listando solo los folios en ese estado.
              </p>
            </div>

            {/* List of Stages */}
            <div className="space-y-2">
              {/* Option 0: Todas las Estaciones */}
              <div
                onClick={() => setSelectedFaseNumber(0)}
                className={`p-3 rounded-2xl transition-all cursor-pointer flex items-center justify-between gap-3 border ${
                  selectedFaseNumber === 0 
                    ? 'bg-[#2d1a1e] border-indigo-500 shadow-md ring-1 ring-indigo-500/40' 
                    : 'bg-[#18191d] border-slate-800 hover:bg-slate-800/80 hover:border-slate-700'
                }`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`h-9 w-9 rounded-xl flex items-center justify-center shrink-0 font-extrabold text-xs ${
                    selectedFaseNumber === 0 ? 'bg-indigo-600 text-white' : 'bg-slate-800 text-slate-300'
                  }`}>
                    <Compass className="h-4 w-4" />
                  </div>
                  <div className="min-w-0">
                    <span className="text-[10px] font-bold text-indigo-400 block tracking-wider uppercase">VISTA GENERAL</span>
                    <h4 className="text-xs font-black text-white truncate">Todas las Estaciones</h4>
                  </div>
                </div>

                <span className={`px-2.5 py-1 rounded-lg text-xs font-mono font-extrabold shrink-0 ${
                  clientFolios.length > 0 ? 'bg-indigo-600/30 text-indigo-300 border border-indigo-500/40' : 'bg-slate-800 text-slate-500'
                }`}>
                  {clientFolios.length}
                </span>
              </div>

              {STAGES.map((st) => {
                const isSelected = selectedFaseNumber === st.faseNumber;
                const count = stageCounts[st.faseNumber] || 0;

                const qrThumbnailUrl = `https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=${encodeURIComponent(
                  `${window.location.origin}${window.location.pathname}?terminal=Fase${st.faseNumber}&client=${activeClient?.id || 'ALL'}`
                )}`;

                return (
                  <div
                    key={st.faseNumber}
                    onClick={() => setSelectedFaseNumber(st.faseNumber)}
                    className={`p-3 rounded-2xl transition-all cursor-pointer flex items-center justify-between gap-3 border ${
                      isSelected 
                        ? 'bg-[#2d1a1e] border-red-600 shadow-md ring-1 ring-red-500/40' 
                        : 'bg-[#18191d] border-slate-800 hover:bg-slate-800/80 hover:border-slate-700'
                    }`}
                  >
                    <div className="space-y-1 min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded-md text-[10px] font-black uppercase font-mono ${
                          isSelected ? 'bg-red-600 text-white' : 'bg-slate-800 text-slate-300'
                        }`}>
                          Estación
                        </span>

                        {count > 0 && (
                          <span className="bg-red-700 text-white text-[10px] font-black px-2 py-0.5 rounded-md font-mono animate-pulse">
                            {count} folios
                          </span>
                        )}
                      </div>

                      <h4 className="text-xs font-black text-white leading-none pt-0.5">
                        {st.label}
                      </h4>

                      <p className="text-[10px] text-slate-400 line-clamp-1 leading-tight">
                        {st.subtitle}
                      </p>
                    </div>

                    {/* QR Thumbnail */}
                    <div className="h-10 w-10 bg-white p-0.5 rounded-lg shrink-0 border border-slate-700">
                      <img src={qrThumbnailUrl} alt="QR Thumbnail" className="w-full h-full object-contain" />
                    </div>

                    <div className={`p-1.5 rounded-lg shrink-0 ${
                      isSelected ? 'bg-red-600 text-white' : 'bg-slate-800 text-slate-400'
                    }`}>
                      <ChevronRight className="h-4 w-4" />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Printable Card Box */}
          <div className="bg-white rounded-3xl p-5 border border-slate-200/90 shadow-xs space-y-4">
            <div className="flex items-center justify-between gap-2 border-b border-slate-100 pb-3">
              <span className="text-xs font-black uppercase tracking-wider text-slate-700">
                IMPRIMIR TARJETA DE CONTROL
              </span>

              <button
                onClick={handlePrintStationCard}
                className="border border-red-200 hover:border-red-400 bg-red-50 text-red-700 hover:bg-red-100 text-[11px] font-extrabold px-3 py-1.5 rounded-xl transition-all flex items-center gap-1.5 cursor-pointer"
              >
                <Printer className="h-3.5 w-3.5" /> Imprimir Tarjeta Carta (8.5" x 11")
              </button>
            </div>

            <div className="border-2 border-slate-200 rounded-2xl p-4 text-center space-y-3 bg-slate-50/50">
              <div className="space-y-1">
                <span className="text-red-700 font-black text-xs uppercase tracking-widest block font-mono">
                  SIC PRO SNF INDUSTRIAL SERVICES
                </span>
                <h4 className="text-sm font-black text-slate-900 tracking-tight uppercase">
                  ESTACIÓN: {currentStageDef.label.toUpperCase()}
                </h4>
                <p className="text-[11px] text-slate-500">
                  Escanear para cargar folios de: <strong className="text-slate-800">{activeClient ? activeClient.name : 'TODOS'}</strong>
                </p>
              </div>

              <div className="p-3 bg-white rounded-2xl border border-slate-200 inline-block shadow-xs">
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(
                    `${window.location.origin}${window.location.pathname}?terminal=Fase${selectedFaseNumber}&client=${activeClient?.id || 'ALL'}`
                  )}`}
                  alt="QR Code Station"
                  className="w-36 h-36 mx-auto object-contain"
                />
              </div>

              <p className="text-[10px] text-slate-400 leading-tight px-2">
                Validez operacional garantizada. Al escanear con su tableta o celular institucional de piso, se desplegarán todos los folios en cuarentena para su verificación y registro fotográfico.
              </p>
            </div>
          </div>

        </div>

        {/* RIGHT COLUMN: PANTALLA DE PISO (Col 8) - Matching image exactly */}
        <div className="lg:col-span-8 space-y-4">
          
          {/* Header Panel */}
          <div className="bg-white rounded-2xl p-4 sm:p-5 border border-slate-200/80 shadow-xs flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full bg-red-600 animate-pulse"></span>
                <h2 className="text-base font-black text-slate-900 uppercase font-display tracking-tight">
                  PANTALLA DE PISO: {currentStageDef.label.toUpperCase()}
                </h2>
              </div>
              <p className="text-xs text-slate-500 mt-0.5">
                Lista de folios y control de paso para el cliente <strong className="text-slate-800">{activeClient ? activeClient.name : 'TODOS LOS CLIENTES'}</strong>
              </p>
            </div>

            {/* Search input */}
            <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2 w-full md:w-72 focus-within:ring-2 focus-within:ring-red-500/20 focus-within:border-red-500 transition-all">
              <Search className="h-4 w-4 text-slate-400 shrink-0" />
              <input
                type="text"
                placeholder="Buscador de folios..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-transparent text-xs text-slate-800 placeholder-slate-400 focus:outline-hidden w-full font-medium"
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery('')} className="text-slate-400 hover:text-slate-600">
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* Subheader Folios Count */}
          <div className="text-xs font-black text-slate-400 uppercase tracking-wider px-1">
            FOLIOS EN ESTE PROCESO ({stageFolios.length})
          </div>

          {/* Folios List / Selectors */}
          {stageFolios.length === 0 ? (
            <div className="bg-white rounded-3xl p-12 text-center border border-slate-200/80 shadow-xs space-y-3">
              <div className="h-12 w-12 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto border border-emerald-200">
                <CheckCircle2 className="h-7 w-7" />
              </div>
              <h3 className="text-sm font-bold text-slate-800">
                ¡Ninguno lote de carga pendiente en este proceso!
              </h3>
              <p className="text-xs text-slate-500 max-w-md mx-auto">
                Todos los folios de {activeClient ? activeClient.name : 'los clientes'} para esta etapa han sido despachados exitosamente.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              
              {/* Selectable Folios Bar */}
              <div className="flex flex-col gap-2">
                {stageFolios.map((folio) => {
                  const mainItem = folio.items[0];
                  const totalPieces = folio.items.reduce((acc, it) => acc + (it.quantity || 0), 0);
                  const weight = (folio as any).totalWeight || 0;
                  const isSelected = activeFolio?.id === folio.id;

                  return (
                    <button
                      key={folio.id}
                      onClick={() => setSelectedFolioId(folio.id)}
                      className={`w-full text-left rounded-2xl p-3.5 sm:p-4 border transition-all flex items-center justify-between gap-3 cursor-pointer ${
                        isSelected 
                          ? 'bg-[#f5f3ff] border-[#6366f1] ring-2 ring-[#6366f1]/20 shadow-xs' 
                          : 'bg-white border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`p-2 rounded-xl shrink-0 ${isSelected ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-600'}`}>
                          <Package className="h-4 w-4" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`font-extrabold text-xs sm:text-sm font-mono tracking-tight ${isSelected ? 'text-[#4f46e5]' : 'text-slate-900'}`}>
                              FOLIO: #{folio.id}
                            </span>
                          </div>
                          <p className="text-xs text-slate-500 truncate font-medium">
                            {totalPieces}x {mainItem?.description || 'SKIDS (Piece)'}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 shrink-0">
                        <span className="bg-[#4f46e5] text-white font-extrabold text-[11px] px-2.5 py-1 rounded-lg font-mono">
                          {weight} KG
                        </span>
                        <ChevronRight className={`h-4 w-4 ${isSelected ? 'text-[#4f46e5]' : 'text-slate-400'}`} />
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* MAIN CONTROL FORM PANEL (Fórmula de Control) - Process Confirmation for Internal Operators */}
              {activeFolio && (
                <motion.div
                  key={activeFolio.id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-3xl p-5 sm:p-7 border border-slate-200/90 shadow-xs space-y-6"
                >
                  
                  {/* Top Bar inside Form */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4 border-b border-slate-100">
                    <div className="space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-sm sm:text-base font-black text-slate-900 tracking-tight">
                          Fórmula de Control: #{activeFolio.id}
                        </h3>
                        <span className="bg-slate-100 border border-slate-200 text-indigo-700 font-extrabold text-[10px] px-2 py-0.5 rounded-md flex items-center gap-1 font-mono uppercase">
                          <Barcode className="h-3 w-3" /> CÓDIGO DE BARRAS
                        </span>
                        <button
                          type="button"
                          onClick={() => setShowFolioDetailModal(true)}
                          className="bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 text-indigo-700 text-[11px] font-bold px-2.5 py-0.5 rounded-md transition-all flex items-center gap-1 cursor-pointer"
                        >
                          <Eye className="h-3 w-3" /> Ver Detalle de Folio
                        </button>
                        <button
                          type="button"
                          onClick={() => downloadFolioPDF(activeFolio, { hidePrices: isPublicView })}
                          className="bg-slate-100 hover:bg-slate-200 border border-slate-300 text-slate-700 text-[11px] font-bold px-2.5 py-0.5 rounded-md transition-all flex items-center gap-1 cursor-pointer"
                        >
                          <FileText className="h-3 w-3 text-red-600" /> PDF
                        </button>
                      </div>
                      <p className="text-xs font-semibold text-slate-500">
                        Destino: {activeFolio.destinationCity || 'San Luis'}
                      </p>
                    </div>

                    <div className="bg-slate-50 border border-slate-200 px-3 py-1.5 rounded-xl text-xs font-bold text-slate-700 flex items-center gap-1.5 self-start sm:self-auto max-w-md shadow-2xs">
                      <MapPin className="h-3.5 w-3.5 text-red-600 shrink-0" />
                      <span className="truncate" title={resolvePatioAddress(activeFolio)}>
                        Patio: {resolvePatioAddress(activeFolio)}
                      </span>
                    </div>
                  </div>

                  {/* Stage Transition Box (ETAPA ACTUAL -> PRÓXIMA ETAPA) */}
                  <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 flex items-center justify-center gap-4 sm:gap-8 text-center">
                    <div>
                      <span className="text-[9px] font-black uppercase text-slate-400 tracking-wider block">
                        ETAPA ACTUAL
                      </span>
                      <span className="text-xs sm:text-sm font-black text-slate-800 uppercase font-mono mt-0.5 block">
                        {getStageLabel(activeFolio.status)}
                      </span>
                    </div>

                    <div className="h-8 w-8 rounded-full bg-slate-200 text-slate-700 flex items-center justify-center shrink-0">
                      <ArrowRight className="h-4 w-4" />
                    </div>

                    <div>
                      <span className="text-[9px] font-black uppercase text-emerald-600 tracking-wider block bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-200 inline-block">
                        PRÓXIMA ETAPA
                      </span>
                      <span className="text-xs sm:text-sm font-black text-emerald-700 uppercase font-mono mt-0.5 block">
                        {getStageLabel(getNextStage(activeFolio.status, activeFolio.hasGranallado, activeFolio.processType))}
                      </span>
                    </div>
                  </div>

                  {isPublicView ? (
                    <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 text-center space-y-2">
                      <div className="h-9 w-9 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center mx-auto">
                        <Eye className="h-4 w-4" />
                      </div>
                      <h4 className="text-xs font-black uppercase tracking-wider text-slate-700">Modo Informativo / Consulta de Cliente</h4>
                      <p className="text-xs text-slate-500 max-w-lg mx-auto">
                        La confirmación de fases, estampas de firma de piso y captura de bitácoras están reservadas para los operadores técnicos de planta autorizados.
                      </p>
                    </div>
                  ) : (
                    <>
                      {/* Operator & Server Time fields */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        
                        {/* Operator Name */}
                        <div className="space-y-1.5">
                          <label className="text-[10px] font-extrabold text-slate-500 uppercase tracking-wider block">
                            NOMBRE DEL OPERADOR AUTORIZADO *
                          </label>

                          <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 focus-within:ring-2 focus-within:ring-red-500/20 focus-within:border-red-500">
                            <User className="h-4 w-4 text-slate-400 shrink-0" />
                            <input
                              type="text"
                              value={operatorName}
                              onChange={(e) => setOperatorName(e.target.value)}
                              placeholder="Nombre del operador..."
                              className="bg-transparent text-xs font-black text-slate-900 focus:outline-hidden w-full uppercase"
                            />
                          </div>

                          <p className="text-[10px] text-slate-400 leading-tight pt-0.5">
                            Este nombre quedará impreso de forma indeleble en el histórico de trazabilidad tributaria del cliente.
                          </p>
                        </div>

                        {/* Server Time Sync */}
                        <div className="space-y-1.5">
                          <label className="text-[10px] font-extrabold text-slate-500 uppercase tracking-wider block">
                            SINCRONIZACIÓN DE TIEMPO (SERVIDOR)
                          </label>

                          <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs">
                            <div className="flex items-center gap-2 font-bold text-slate-700">
                              <Clock className="h-4 w-4 text-emerald-600 shrink-0 animate-pulse" />
                              <span>Hora aproximada de red:</span>
                            </div>
                            <span className="font-mono font-black text-slate-900">{liveTimeString || '10:33 a.m.'}</span>
                          </div>

                          <p className="text-[10px] text-slate-400 leading-tight pt-0.5">
                            El momento preciso es estampado al milisegundo por el motor de transacciones de Firebase.
                          </p>
                        </div>

                      </div>

                      {/* Evidence Photo Section */}
                      <div className="space-y-3 pt-2">
                        <label className="text-[10px] font-extrabold text-slate-500 uppercase tracking-wider block">
                          TOMA DE FOTO / REGISTRO DE EVIDENCIA FOTOGRÁFICA EN PISO
                        </label>

                        {/* Camera view if active */}
                        {isCameraActive ? (
                          <div className="relative bg-slate-950 rounded-2xl overflow-hidden aspect-video max-w-md mx-auto shadow-xl border border-slate-800">
                            <video ref={videoRef} autoPlay playsInline className="w-full h-full object-cover" />
                            <div className="absolute bottom-3 inset-x-0 flex justify-center gap-3">
                              <button
                                type="button"
                                onClick={takePhoto}
                                className="bg-red-600 hover:bg-red-500 text-white text-xs font-black px-5 py-2.5 rounded-xl flex items-center gap-2 cursor-pointer shadow-lg"
                              >
                                <Camera className="h-4 w-4" /> Capturar Foto
                              </button>
                              <button
                                type="button"
                                onClick={stopCamera}
                                className="bg-slate-800 text-white text-xs font-bold px-4 py-2.5 rounded-xl cursor-pointer"
                              >
                                Cancelar
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="border-2 border-dashed border-slate-200 rounded-2xl p-6 text-center bg-slate-50/50 space-y-4">
                            
                            {/* Display uploaded/captured thumbnails if present */}
                            {evidencePhotos.length > 0 ? (
                              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                {evidencePhotos.map((photo, idx) => (
                                  <div key={idx} className="relative aspect-video rounded-xl overflow-hidden border border-slate-300 group bg-slate-900">
                                    <img src={photo} alt={`Evidencia ${idx + 1}`} className="w-full h-full object-cover" />
                                    <button
                                      type="button"
                                      onClick={() => removePhoto(idx)}
                                      className="absolute top-1 right-1 bg-red-600 text-white p-1 rounded-md opacity-90 hover:opacity-100 transition-opacity"
                                    >
                                      <Trash2 className="h-3 w-3" />
                                    </button>
                                    <span className="absolute bottom-1 left-1 bg-slate-950/80 text-white font-mono text-[9px] px-1.5 py-0.5 rounded-md">
                                      #{idx + 1}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="space-y-1 py-2">
                                <FileText className="h-8 w-8 text-slate-300 mx-auto" />
                                <p className="text-xs font-bold text-slate-500">Sin fotos de evidencia</p>
                                <p className="text-[10px] text-slate-400">Permite hasta 7 fotos registradas</p>
                              </div>
                            )}

                            {/* Action Buttons */}
                            <div className="flex flex-wrap items-center justify-center gap-3 pt-1">
                              <button
                                type="button"
                                onClick={startCamera}
                                className="bg-red-700 hover:bg-red-600 text-white text-xs font-extrabold px-5 py-2.5 rounded-full transition-all shadow-sm flex items-center gap-2 cursor-pointer"
                              >
                                <Camera className="h-4 w-4" /> TOMA DE FOTO EN VIVO
                              </button>

                              <label className="bg-slate-200 hover:bg-slate-300 text-slate-800 text-xs font-extrabold px-5 py-2.5 rounded-full transition-all flex items-center gap-2 cursor-pointer">
                                <Upload className="h-4 w-4 text-slate-600" /> SUBIR IMÁGENES
                                <input
                                  type="file"
                                  accept="image/*"
                                  multiple
                                  className="hidden"
                                  onChange={handleFileUpload}
                                />
                              </label>
                            </div>

                          </div>
                        )}

                        <p className="text-[10px] font-bold text-red-600 leading-tight">
                          Tome o suba fotos del sello de flete, piezas decapadas o bitácoras de entrega. Máximo 7 fotos permitidas. Se integrarán automáticamente en el reporte PDF.
                        </p>
                      </div>

                      {/* Observations Section */}
                      <div className="space-y-1.5">
                        <label className="text-[10px] font-extrabold text-slate-500 uppercase tracking-wider block">
                          NOVEDADES, OBSERVACIONES DE PATIO O REPORTE QUÍMICO
                        </label>

                        <textarea
                          rows={2}
                          value={transitionNotes}
                          onChange={(e) => setTransitionNotes(e.target.value)}
                          placeholder="Ingrese precintos de tolvas, observaciones adicionales o notas de liberación..."
                          className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs text-slate-900 focus:outline-hidden focus:ring-2 focus:ring-red-500"
                        />
                      </div>

                      {/* Bottom Right Confirm Action Button */}
                      <div className="flex justify-end pt-2">
                        <button
                          type="button"
                          onClick={handleConfirmTransition}
                          className="bg-emerald-600 hover:bg-emerald-500 active:scale-95 text-white font-extrabold text-xs sm:text-sm px-6 py-3 rounded-full transition-all shadow-md flex items-center gap-2 cursor-pointer"
                        >
                          <CheckCircle2 className="h-5 w-5" /> CONFIRMAR TRÁNSITO / FIRMA EN PISO
                        </button>
                      </div>
                    </>
                  )}

                </motion.div>
              )}

            </div>
          )}

        </div>

      </div>

      {/* PHASE CHANGE ANIMATION OVERLAY (ANIMACIÓN DE CAMBIO DE FASE) */}
      <AnimatePresence>
        {animationModalData && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md">
            <motion.div
              initial={{ opacity: 0, scale: 0.8, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: -30 }}
              transition={{ type: 'spring', stiffness: 350, damping: 25 }}
              className="bg-[#18191d] text-white rounded-3xl border border-slate-700 shadow-2xl max-w-lg w-full p-6 sm:p-8 text-center space-y-6 relative overflow-hidden"
            >
              
              {/* Background Glow Animation */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-64 bg-emerald-500/15 rounded-full blur-3xl pointer-events-none animate-pulse"></div>

              {/* Animated Success Ring */}
              <div className="relative mx-auto w-20 h-20 flex items-center justify-center">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: [0, 1.3, 1] }}
                  transition={{ duration: 0.6, ease: 'easeOut' }}
                  className="h-20 w-20 rounded-full bg-emerald-500/20 border-2 border-emerald-400 flex items-center justify-center text-emerald-400 shadow-lg"
                >
                  <Check className="h-10 w-10 stroke-[3]" />
                </motion.div>

                <motion.div
                  animate={{ scale: [1, 1.5, 1], opacity: [0.8, 0, 0.8] }}
                  transition={{ repeat: Infinity, duration: 2 }}
                  className="absolute inset-0 rounded-full border border-emerald-400/50"
                ></motion.div>
              </div>

              {/* Title & Banner */}
              <div className="space-y-2 relative z-10">
                <span className="px-3 py-1 bg-emerald-950/90 border border-emerald-600/80 text-emerald-400 text-[11px] font-black uppercase rounded-full tracking-widest font-mono">
                  ¡CAMBIO DE FASE EXITOSO!
                </span>

                <h3 className="text-xl sm:text-2xl font-black font-display tracking-tight text-white pt-1">
                  TRÁNSITO Y FIRMA EN PISO REGISTRADOS
                </h3>

                <p className="text-xs text-slate-300">
                  Folio <strong className="text-emerald-400 font-mono">#{animationModalData.folio.id}</strong> procesado en tiempo real por el operador <strong className="text-white">{animationModalData.operator}</strong>.
                </p>
              </div>

              {/* Stage Morphing Motion graphic */}
              <div className="bg-[#23242a] border border-slate-800 rounded-2xl p-4 flex items-center justify-around gap-2 font-mono">
                <div>
                  <span className="text-[10px] text-slate-400 font-bold uppercase block">Fase Anterior</span>
                  <span className="text-xs font-black text-slate-300">{animationModalData.oldStageLabel}</span>
                </div>

                <motion.div
                  animate={{ x: [0, 8, 0] }}
                  transition={{ repeat: Infinity, duration: 1.2 }}
                  className="text-emerald-400"
                >
                  <ArrowRight className="h-5 w-5" />
                </motion.div>

                <div>
                  <span className="text-[10px] text-emerald-400 font-bold uppercase block">Nueva Fase</span>
                  <span className="text-xs font-black text-emerald-400">{animationModalData.nextStageLabel}</span>
                </div>
              </div>

              {/* Dismiss Button */}
              <button
                onClick={() => setAnimationModalData(null)}
                className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-extrabold text-xs py-3.5 rounded-xl transition-all shadow-lg cursor-pointer"
              >
                Aceptar y Continuar
              </button>

            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* FOLIO DETAIL MODAL OVERLAY */}
      <AnimatePresence>
        {showFolioDetailModal && activeFolio && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm overflow-y-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white rounded-3xl border border-slate-200 shadow-2xl max-w-3xl w-full p-6 sm:p-8 space-y-6 relative max-h-[90vh] overflow-y-auto my-auto"
            >
              {/* Top Modal Header */}
              <div className="flex items-center justify-between border-b border-slate-100 pb-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="bg-indigo-100 text-indigo-700 font-extrabold text-[10px] px-2.5 py-0.5 rounded-md font-mono uppercase">
                      FOLIO DE CARGA
                    </span>
                    <h3 className="text-xl font-black text-slate-900 tracking-tight font-display">
                      Detalle de Folio #{activeFolio.id}
                    </h3>
                  </div>
                  <p className="text-xs text-slate-500">
                    Cliente: <strong className="text-slate-800">{activeFolio.clientName}</strong>
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => setShowFolioDetailModal(false)}
                  className="h-9 w-9 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-600 flex items-center justify-center transition-all cursor-pointer"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Grid Summary Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="bg-slate-50 border border-slate-200 rounded-2xl p-3 text-center">
                  <span className="text-[10px] font-bold text-slate-400 uppercase block">ESTADO ACTUAL</span>
                  <span className="text-xs font-black text-indigo-700 font-mono mt-0.5 block uppercase">
                    {getStageLabel(activeFolio.status)}
                  </span>
                </div>

                <div className="bg-slate-50 border border-slate-200 rounded-2xl p-3 text-center">
                  <span className="text-[10px] font-bold text-slate-400 uppercase block">PESO TOTAL</span>
                  <span className="text-xs font-black text-slate-900 font-mono mt-0.5 block">
                    {(activeFolio as any).totalWeight || 0} KG
                  </span>
                </div>

                <div className="bg-slate-50 border border-slate-200 rounded-2xl p-3 text-center">
                  <span className="text-[10px] font-bold text-slate-400 uppercase block">PROCESO ASIGNADO</span>
                  <span className={`text-xs font-black font-mono mt-0.5 block ${
                    activeFolio.processType === 'DECAPADO_QUIMICO'
                      ? 'text-blue-600'
                      : (activeFolio.processType === 'DECAPADO_GRANALLADORA' || activeFolio.hasGranallado)
                        ? 'text-purple-600'
                        : 'text-amber-600'
                  }`}>
                    {activeFolio.processType === 'DECAPADO_QUIMICO'
                      ? 'QUÍMICO (➔ LAVADO)'
                      : (activeFolio.processType === 'DECAPADO_GRANALLADORA' || activeFolio.hasGranallado)
                        ? 'GRANALLADORA (DIRECTO)'
                        : 'TÉRMICO (➔ LAVADO)'}
                  </span>
                </div>

                <div className="bg-slate-50 border border-slate-200 rounded-2xl p-3 text-center">
                  <span className="text-[10px] font-bold text-slate-400 uppercase block">FECHA REGISTRO</span>
                  <span className="text-xs font-black text-slate-800 font-mono mt-0.5 block">
                    {activeFolio.date}
                  </span>
                </div>
              </div>

              {/* Items / Materials Table */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-black text-slate-800 uppercase tracking-wider flex items-center gap-1.5">
                    <Package className="h-4 w-4 text-indigo-600" /> DESGLOSE DE MATERIALES ({activeFolio.items.length})
                  </h4>
                </div>

                <div className="border border-slate-200 rounded-2xl overflow-hidden shadow-2xs">
                  <table className="w-full text-left text-xs border-collapse">
                    <thead className="bg-slate-100/80 text-slate-600 font-extrabold uppercase text-[10px] tracking-wider border-b border-slate-200">
                      <tr>
                        <th className="p-3">Cant</th>
                        <th className="p-3">Descripción / Material</th>
                        <th className="p-3">N° Parte</th>
                        <th className="p-3 text-right">Peso Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 font-medium text-slate-800">
                      {activeFolio.items.map((item, idx) => (
                        <tr key={idx} className="hover:bg-slate-50/80 transition-colors">
                          <td className="p-3 font-mono font-black text-indigo-700">{item.quantity}x</td>
                          <td className="p-3">
                            <span className="font-bold block text-slate-900">{item.description || 'SKIDS'}</span>
                            {item.materialType && (
                              <span className="text-[10px] text-slate-400 block">{item.materialType}</span>
                            )}
                          </td>
                          <td className="p-3 font-mono text-slate-600">{item.partNumber || 'S/N'}</td>
                          <td className="p-3 text-right font-mono font-bold text-slate-900">
                            {(item.unitWeight || 0) * (item.quantity || 1)} KG
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Transport & Logistics Section */}
              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 space-y-2">
                <h4 className="text-xs font-black text-slate-800 uppercase tracking-wider flex items-center gap-1.5">
                  <Truck className="h-4 w-4 text-red-600" /> LOGÍSTICA Y TRANSPORTE
                </h4>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                  <div>
                    <span className="text-slate-400 text-[10px] block font-bold uppercase">LÍNEA DE TRANSPORTE</span>
                    <span className="font-extrabold text-slate-800">{activeFolio.carrier || 'FLETES ESPECIALIZADOS DEL CENTRO'}</span>
                  </div>

                  <div>
                    <span className="text-slate-400 text-[10px] block font-bold uppercase">CHOFER / UNIDAD</span>
                    <span className="font-extrabold text-slate-800">{activeFolio.driverName || 'CARLOS MENDOZA'} (Placas: {activeFolio.licensePlates || '482-UM-2'})</span>
                  </div>

                  <div>
                    <span className="text-slate-400 text-[10px] block font-bold uppercase">ORIGEN</span>
                    <span className="font-extrabold text-slate-800">{activeFolio.originCity || 'Puebla / Planta BMW'}</span>
                  </div>

                  <div>
                    <span className="text-slate-400 text-[10px] block font-bold uppercase">DESTINO</span>
                    <span className="font-extrabold text-slate-800">{activeFolio.destinationCity || 'San Luis Potosí'}</span>
                  </div>
                </div>
              </div>

              {/* History Timeline */}
              <div className="space-y-2">
                <h4 className="text-xs font-black text-slate-800 uppercase tracking-wider flex items-center gap-1.5">
                  <Activity className="h-4 w-4 text-emerald-600" /> HISTORIAL DE TRAZABILIDAD Y FIRMAS EN PISO
                </h4>

                <div className="space-y-2 border-l-2 border-indigo-200 ml-2 pl-4 py-1">
                  {activeFolio.history.map((h, idx) => (
                    <div key={idx} className="relative space-y-0.5">
                      <div className="absolute -left-[21px] top-1.5 h-2.5 w-2.5 rounded-full bg-indigo-600 ring-4 ring-indigo-100"></div>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs font-black text-slate-900 uppercase font-mono">
                          {getStageLabel(h.stage)}
                        </span>
                        <span className="text-[10px] text-slate-400 font-mono">
                          {new Date(h.timestamp).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-[11px] text-slate-600">
                        Operador: <strong className="text-slate-800">{h.operatorName}</strong> — {h.notes}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Footer Actions */}
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => downloadFolioPDF(activeFolio, { hidePrices: isPublicView })}
                  className="w-full sm:w-auto bg-slate-900 hover:bg-slate-800 text-white font-extrabold text-xs px-5 py-2.5 rounded-xl transition-all flex items-center justify-center gap-2 cursor-pointer shadow-md"
                >
                  <Printer className="h-4 w-4 text-red-400" /> Descargar Reporte PDF
                </button>

                <button
                  type="button"
                  onClick={() => setShowFolioDetailModal(false)}
                  className="w-full sm:w-auto bg-slate-100 hover:bg-slate-200 text-slate-700 font-extrabold text-xs px-6 py-2.5 rounded-xl transition-all cursor-pointer"
                >
                  Cerrar Detalle
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

    </div>
  );
}
